import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const DentalImplantsSpecial = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>FREE Dental Implant Consultation | $475 Value | Fort Worth Dental</title>
        <meta name="description" content="FREE dental implant consultation including X-rays - a $475 value! Expert implant dentist Dr. Mund. Affordable dental implants in Fort Worth TX. Call (817) 985-3337." />
        <link rel="canonical" href="https://fortworthdental.com/specials/dental-implants" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Dental Implants – Free Consultation including X-Rays</h1>
          <p className="text-2xl sm:text-3xl font-semibold text-yellow-300">$475.00 Value!</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Main Content Section */}
          <section className="mb-12">
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                Fort Worth Dental is now offering <strong>FREE CONSULTATION plus X-Rays</strong> for new dental implant patients.
              </p>
              
              <p className="text-lg leading-relaxed mb-6">
                This limited time offer for patients in <strong>Fort Worth, Aledo, Benbrook, and surrounding areas</strong> includes 
                a FREE comprehensive exam and full mouth series of X-Rays (a $475 value). 
                <strong> You will also receive a written treatment plan with all costs outlined.</strong>
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Dr. Mund is a diplomate of the American Board of Oral Implantology/ Implant Dentistry and has successfully placed 
                thousands of dental implants over the past 25 years. He has been selected 13 times as a "<strong>Top Dentist</strong>" 
                by his peers in <em>Fort Worth Texas Magazine</em> and three times by <em>360 West Magazine</em>. Dr. Mund is also a 
                fellow of the American Academy of Implant Dentistry and the International Congress of Oral Implantologists.
              </p>
            </div>
          </section>

          {/* Image Section */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/dental-implant-hero.jpg" 
                  alt="Dental Implant Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/bone-grafting-new-1.jpg'}
                />
              </div>
              <div>
                <img 
                  src="/images/services/bone-grafting-new-1.jpg" 
                  alt="Dental Implant Model" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/crowns-new-1.jpg'}
                />
              </div>
            </div>
          </section>

          {/* YouTube Videos Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Learn More About Dental Implants</h2>
            <p className="text-gray-600 text-center mb-8">Watch these informative videos about dental implant procedures and benefits</p>
            
            {/* First row - 2 videos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/qPyKn2Fic0w"
                  title="Dental Implants Video 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/sozRchIFTFw"
                  title="Dental Implants Video 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
            
            {/* Second row - 3 videos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/zzTjgsc_np8"
                  title="Dental Implants Video 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/TMMjpaZ0sA0"
                  title="Dental Implants Video 4"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/iDTXjvaiPqs"
                  title="Dental Implants Video 5"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </section>

          {/* Click Here Links Section */}
          <section className="mb-12 space-y-6">
            <div className="bg-teal-50 p-6 rounded-lg">
              <p className="text-lg text-gray-700 mb-4">
                Still need to find out more about dental implants and implant dentists in Fort Worth?
              </p>
              <a 
                href="https://fortworthdental.com/finding-the-best-dental-implant-dentists-in-fort-worth-tx" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Click Here
              </a>
            </div>

            <div className="bg-purple-50 p-6 rounded-lg">
              <p className="text-lg text-gray-700 mb-4">
                For more information on financing and payment options
              </p>
              <a 
                href="https://fortworthdental.com/form-finances" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Click Here
              </a>
            </div>
          </section>

          {/* Special Offer Box */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-teal-500 to-teal-700 text-white p-8 rounded-lg text-center">
              <h2 className="text-3xl font-bold mb-4">Special Offer: FREE Consultation + X-Rays</h2>
              <p className="text-xl mb-2">A $475 Value - Yours FREE!</p>
              <p className="text-lg opacity-90 mb-6">Limited time offer for new dental implant patients</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <a 
                  href="tel:8178544329" 
                  className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
                >
                  Call Implant Center: (817) 854-4329
                </a>
                <Link 
                  to="/implant-center" 
                  className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 transition-colors"
                >
                  Learn More About Implants
                </Link>
              </div>
            </div>
          </section>

          {/* What's Included Section */}
          <section className="mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-6">What's Included in Your FREE Consultation:</h3>
            <ul className="space-y-4 text-gray-700 text-lg">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Complete oral examination by our implant specialist, Dr. Mund</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Full mouth series of digital X-rays to evaluate bone structure</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Personalized treatment plan</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Written cost estimate with all costs outlined</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Financing options discussion</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Answers to all your dental implant questions</span>
              </li>
            </ul>
          </section>

          {/* Appointment Form */}
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Schedule Your FREE Dental Implant Consultation
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Fill out the form below or call us at <a href="tel:8178544329" className="text-teal-600 font-semibold">(817) 854-4329</a>
            </p>
            <AppointmentForm />
          </section>

        </div>
      </div>
    </div>
  );
};

export default DentalImplantsSpecial;
